/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

interface MediaRendererProps {
  media: {
    value?: string | any;
    src?: string;
    mimeType?: string;
    extension?: string;
    alt?: string;
  };
  className?: string;
  videoProps?: React.VideoHTMLAttributes<HTMLVideoElement>;
  imageStyle?: React.CSSProperties;
  asBackground?: boolean; // If false, renders images as img tags instead of background divs
  priority?: boolean; // When true, adds fetchPriority="high" for LCP optimization
}

/**
 * MediaRenderer Component
 * 
 * Renders different media types based on the provided media object:
 * - YouTube videos: when media.value is a string (YouTube video ID)
 * - Video files: when mimeType starts with "video/"
 * - Images: for all other cases
 * 
 * @param media - Media object that can be:
 *   - Full media object: { src: "...", mimeType: "video/mp4", ... }
 *   - YouTube video: { value: "youtubeVideoId" }
 * @param className - Additional CSS classes to apply
 * @param videoProps - Additional props to pass to video element
 * @param imageStyle - Additional inline styles for image background
 */
const MediaRenderer: React.FC<MediaRendererProps> = ({
  media,
  className = "",
  videoProps = {},
  imageStyle = {},
  asBackground = true,
  priority = false,
}) => {
  

  // Handle nested value structure (e.g., when media.value is an object)
  const mediaValue = media?.value;
  const isValueAnObject = mediaValue && typeof mediaValue === "object" && !Array.isArray(mediaValue);
  
  // Extract properties - check both direct properties and nested value object
  const src = media?.src || (isValueAnObject ? mediaValue?.src : null) || "";
  const mimeType = media?.mimeType || (isValueAnObject ? mediaValue?.mimeType : null) || "";
  const extension = media?.extension || (isValueAnObject ? mediaValue?.extension : null) || "";
  const alt = media?.alt || (isValueAnObject ? mediaValue?.alt : null) || "";
  
  // Check if it's a YouTube video
  // YouTube: when value is a string (not an object) and there's no src/mimeType/extension
  // This handles cases like {value: "youtubeVideoId"} or {value: "12345"}
  const hasMediaProperties = !!(media?.src || media?.mimeType || media?.extension);
  const hasNestedMediaProperties = isValueAnObject && !!(mediaValue?.src || mediaValue?.mimeType || mediaValue?.extension);
  
  const isYouTubeVideo =
    typeof mediaValue === "string" &&
    mediaValue.length > 0 &&
    !hasMediaProperties &&
    !hasNestedMediaProperties;

  // Check if it's a regular video file
  const isVideoFile =
    mimeType?.startsWith("video/") ||
    extension?.toLowerCase() === "mp4" ||
    extension?.toLowerCase() === "webm" ||
    extension?.toLowerCase() === "mov";

  // YouTube video rendering
  if (isYouTubeVideo) {
    const youtubeId = typeof mediaValue === "string" ? mediaValue : "";
    const youtubeUrl = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&modestbranding=1&rel=0&showinfo=0`;

    return (
      <div
        className={`absolute top-0 left-0 right-0 bottom-0 w-full h-full overflow-hidden -z-10 ${className}`}
      >
        <iframe
          title="Embedded YouTube video"
          src={youtubeUrl}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          frameBorder="0"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            width: "100vw",
            height: "56.25vw", // 16:9 aspect ratio
            minWidth: "177.78vh", // Ensure it covers height
            minHeight: "100vh", // Ensure it covers height
            pointerEvents: "none",
          }}
        />
      </div>
    );
  }

  // Regular video file rendering
  if (isVideoFile && src) {
    if (asBackground) {
      return (
        <video
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          {...(priority && { fetchPriority: "high" })}
          className={`absolute top-0 left-0 right-0 bottom-0 w-full h-full object-cover -z-10 ${className}`}
          aria-label={alt || "Background video"}
          {...videoProps}
        >
          <track kind="captions" />
        </video>
      );
    }
    
    return (
      <video
        src={src}
        autoPlay
        muted
        loop
        playsInline
        className={`w-full h-full object-contain z-0 ${className}`}
        style={{
          width: "100%",
          height: "100%",
          ...imageStyle,
        }}
        aria-label={alt || "Video content"}
        {...videoProps}
      >
        <track kind="captions" />
      </video>
    );
  }

  // Image rendering (default)
  if (src) {
    // Render as img tag if asBackground is false
    if (!asBackground) {
      return (
        <img
          src={src}
          alt={alt || "Media content"}
          className={`w-full h-full z-0 ${className}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            ...imageStyle,
          }}
        />
      );
    }
    // Render as background div (default)
    return (
      <div
        style={{
          backgroundImage: `url(${src})`,
          ...imageStyle,
        }}
        className={`absolute top-0 left-0 right-0 bottom-0 w-full h-full bg-cover bg-center -z-10 ${className}`}
      />
    );
  }

  // Fallback: return null if no valid media
  return null;
};

export default MediaRenderer;

